import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';

import Img from 'retired/Img';
import CompletedIcon from 'assets/icons/completed.svg';
import { gray3, lightOrange } from 'utils/color-styles';

type DateInput = string | number | Date;

enum DeedEventStatus {
  'published' = 'published',
  'draft' = 'draft',
}

interface DeedStatus {
  ended?: boolean;
  status?: DeedEventStatus;
}

interface DeedDateWithStatusInfo extends DeedStatus {
  date: DateInput;
  timeZone?: string;
}

const container: React.CSSProperties = {
  width: 100,
  height: 'auto',
  borderRight: `1px solid ${lightOrange}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  textTransform: 'uppercase',
  position: 'relative',
};

const styles: Partial<{ [key: string]: React.CSSProperties }> = {
  container,
  p: { verticalAlign: 'middle', display: 'inline' },
  dates: { alignSelf: 'stretch', lineHeight: 'initial' },
  month: { fontSize: 15, fontWeight: 400 },
  day: { fontSize: 24, fontWeight: 500 },
  year: { fontSize: 12, fontWeight: 400, color: gray3 },
  completed: { height: 16, left: 8, top: 8, position: 'absolute' },
};

const getDateInfo = (dateInput: DateInput, passedTimeZone?: string) => {
  const timeZone = passedTimeZone ?? Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;

  const month = formatInTimeZone(dateInput, timeZone, convertTokens('MMM'));
  const day = formatInTimeZone(dateInput, timeZone, convertTokens('DD'));
  const year = formatInTimeZone(dateInput, timeZone, convertTokens('YYYY'));

  return { month, day, year };
};

const StatusIcon = ({ ended, status }: DeedStatus): JSX.Element | null => {
  const showCompleted = ended && status === DeedEventStatus.published;

  return showCompleted ? <Img src={CompletedIcon} alt="" style={styles.completed} /> : null;
};

export const DeedListDateInfoWithStatus = ({
  ended,
  status,
  date,
  timeZone,
}: DeedDateWithStatusInfo): JSX.Element | null => {
  if (!date) {
    return null;
  }

  const { month, day, year } = getDateInfo(date, timeZone);

  return (
    <div style={styles.container}>
      <StatusIcon ended={ended} status={status}></StatusIcon>
      <div style={{ ...styles.month, ...styles.dates }}>
        <p style={styles.p}>{month}</p>
      </div>
      <div style={{ ...styles.day, ...styles.dates }}>
        <p style={styles.p}>{day}</p>
      </div>
      <div style={{ ...styles.year, ...styles.dates }}>
        <p style={styles.p}>{year}</p>
      </div>
    </div>
  );
};

export default DeedListDateInfoWithStatus;
